import { styled } from '@mui/material/styles';
import MuiTable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import { useAggregation } from '../aggregation/AggregationContext';
import { FlatTableAggregations } from '../aggregation/FlatTableAggregations';
import { Footer } from '../components/Footer';
import { TableHead } from '../components/TableHead';
import { Toolbar } from '../components/Toolbar';
import { useDataProvider } from '../DataProviderContext';
import { useIsRowClickable } from '../hooks/useIsRowClickable';
import { Empty } from './components/Empty';
import { GroupRow } from './components/GroupRow';
import { HeadRow } from './components/HeadRow';
import { Row } from './components/Row';
import Skeleton from './components/Skeleton';

const Table = styled(MuiTable, { name: 'FlatTable', slot: 'Table' })({
  // Show borders in table headers when using sticky
  borderCollapse: 'separate',
});

const Root = styled('div', { name: 'FlatTable', slot: 'Root' })({
  width: '100%',
  overflowX: 'auto',
  maxHeight: '100%',
});

export const FlatTable = () => {
  const { table, isLoading, isRowHighlighted, onRowClick } = useDataProvider();
  const isRowClickable = useIsRowClickable();
  const aggregation = useAggregation();
  const showTableContent = aggregation?.showOnlyAggregations !== true;

  if (!table) {
    return null;
  }

  const { rows } = table.getRowModel();

  return (
    <Root>
      <Toolbar />
      <Table>
        <TableHead>
          {table.getHeaderGroups().map((headerGroup) => (
            <HeadRow headerGroup={headerGroup} key={headerGroup.id} />
          ))}
        </TableHead>
        {!isLoading && (
          <>
            {showTableContent && (
              <TableBody>
                {rows.map((row) => {
                  const isClickable = isRowClickable(row);
                  const isHighlight = isRowHighlighted?.(row);
                  const isGrouped =
                    typeof row.getIsGrouped === 'function' &&
                    row.getIsGrouped();

                  if (isGrouped) {
                    return <GroupRow row={row} key={row.id} />;
                  }
                  return (
                    <Row
                      row={row}
                      isClickable={isClickable}
                      isHighlight={isHighlight}
                      onClick={onRowClick}
                      key={row.id}
                    />
                  );
                })}
                {rows.length === 0 && <Empty />}
              </TableBody>
            )}
            <FlatTableAggregations />
          </>
        )}
      </Table>
      {isLoading && <Skeleton />}
      <Footer />
    </Root>
  );
};
